import type { SupabaseClient } from "@supabase/supabase-js";

type HistoryClient = Pick<SupabaseClient, "from">;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Keep the DB's fractional precision. Converting a cursor through Date.toISOString
// would truncate microseconds and lose rows at page boundaries.
const TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?(?:Z|[+-]\d{2}:\d{2})$/;

export interface MirrorHistoryCursor { userId: string; updatedAt: string; id: string }
export interface MirrorMessageCursor { userId: string; sessionId: string; createdAt: string; id: string }
export interface MirrorHistorySession {
  id: string; created_at: string; updated_at: string; title?: string;
  preview?: string; project_id: string | null;
}
export interface MirrorHistoryMessage {
  id: string; role: "user" | "assistant"; content: string; created_at: string;
}
export interface MirrorHistoryPage {
  sessions: MirrorHistorySession[];
  nextCursor: MirrorHistoryCursor | null;
  hasMore: boolean;
  previewUnavailable: boolean;
}
export interface MirrorMessagePage {
  messages: MirrorHistoryMessage[];
  nextCursor: MirrorMessageCursor | null;
  hasMore: boolean;
}

export class MirrorHistoryError extends Error {
  constructor(public readonly code: "invalid_request" | "session_unavailable" | "ownership_mismatch" | "invalid_response" | "load_failed") {
    super(code);
    this.name = "MirrorHistoryError";
  }
}

function isTimestamp(value: unknown): value is string {
  if (typeof value !== "string" || !TIMESTAMP.test(value) || !Number.isFinite(Date.parse(value))) return false;
  const [year, month, day] = value.slice(0, 10).split("-").map(Number);
  const [hour, minute, second] = value.slice(11, 19).split(":").map(Number);
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return month >= 1 && month <= 12 && day >= 1 && day <= days[month - 1] && hour <= 23 && minute <= 59 && second <= 59;
}
function assertId(value: unknown): asserts value is string {
  if (typeof value !== "string" || !UUID.test(value)) throw new MirrorHistoryError("invalid_request");
}
function pageSize(value: number | undefined, fallback: number, max: number): number {
  if (value === undefined) return fallback;
  if (!Number.isInteger(value) || value < 1 || value > max) throw new MirrorHistoryError("invalid_request");
  return value;
}
function rows(data: unknown): Record<string, unknown>[] {
  if (!Array.isArray(data)) throw new MirrorHistoryError("invalid_response");
  if (data.some(row => !row || typeof row !== "object" || Array.isArray(row))) throw new MirrorHistoryError("invalid_response");
  return data as Record<string, unknown>[];
}
function ownedSession(row: Record<string, unknown>, userId: string, sessionId?: string): void {
  if (row.user_id !== userId || row.kind !== "mirror" || (sessionId && row.id !== sessionId)) {
    throw new MirrorHistoryError("ownership_mismatch");
  }
}
function validSession(row: Record<string, unknown>): void {
  if (typeof row.id !== "string" || !UUID.test(row.id) || !isTimestamp(row.created_at) || !isTimestamp(row.updated_at) ||
    (row.title !== null && typeof row.title !== "string") ||
    (row.project_id !== null && (typeof row.project_id !== "string" || !UUID.test(row.project_id)))) {
    throw new MirrorHistoryError("invalid_response");
  }
}

function timestampMicros(value: string): bigint {
  const fraction = /\.(\d+)/.exec(value)?.[1] ?? "";
  return BigInt(Date.parse(value)) * BigInt(1000) + BigInt(fraction.padEnd(6, "0").slice(3, 6));
}

/** Fail closed if a malformed transport would repeat/skip a pagination row. */
function validatePageOrder(data: Record<string, unknown>[], timestampField: string, maxRows: number,
  cursor?: { timestamp: string; id: string } | null): void {
  if (data.length > maxRows) throw new MirrorHistoryError("invalid_response");
  const seen = new Set<string>();
  let previous = cursor ? { timestamp: timestampMicros(cursor.timestamp), id: cursor.id.toLowerCase() } : null;
  for (const row of data) {
    const id = (row.id as string).toLowerCase();
    const timestamp = timestampMicros(row[timestampField] as string);
    if (seen.has(id) || (previous && (timestamp > previous.timestamp || (timestamp === previous.timestamp && id >= previous.id)))) {
      throw new MirrorHistoryError("invalid_response");
    }
    seen.add(id);
    previous = { timestamp, id };
  }
}

/**
 * Bounded history metadata, with a stable tie-breaker instead of OFFSET.
 * Only untitled rows need a preview; each fetch reads at most ONE message.
 * There is no query that downloads every message in the returned conversations.
 */
export async function loadMirrorHistoryPage(client: HistoryClient, options: {
  userId: string; cursor?: MirrorHistoryCursor | null; pageSize?: number;
}): Promise<MirrorHistoryPage> {
  const { userId, cursor } = options;
  assertId(userId);
  const size = pageSize(options.pageSize, 20, 50);
  if (cursor) {
    assertId(cursor.id);
    if (cursor.userId !== userId || !isTimestamp(cursor.updatedAt)) throw new MirrorHistoryError("invalid_request");
  }
  let query = client.from("mirror_sessions")
    .select("id, user_id, kind, created_at, updated_at, title, project_id")
    .eq("user_id", userId).eq("kind", "mirror")
    .order("updated_at", { ascending: false }).order("id", { ascending: false })
    .limit(size + 1);
  if (cursor) query = query.or(`updated_at.lt.${cursor.updatedAt},and(updated_at.eq.${cursor.updatedAt},id.lt.${cursor.id})`);
  const { data, error } = await query;
  if (error) throw new MirrorHistoryError("load_failed");
  const allRows = rows(data);
  for (const row of allRows) { ownedSession(row, userId); validSession(row); }
  validatePageOrder(allRows, "updated_at", size + 1, cursor ? { timestamp: cursor.updatedAt, id: cursor.id } : null);
  const selected = allRows.slice(0, size);
  const hasMore = allRows.length > size;
  let previewUnavailable = false;
  const sessions = await Promise.all(selected.map(async row => {
    const session: MirrorHistorySession = {
      id: row.id as string, created_at: row.created_at as string, updated_at: row.updated_at as string,
      title: typeof row.title === "string" && row.title.trim() ? row.title : undefined,
      project_id: row.project_id as string | null,
    };
    if (session.title) return session;
    const { data: first, error: previewError } = await client.from("mirror_messages")
      .select("id, user_id, session_id, content")
      .eq("user_id", userId).eq("session_id", session.id).eq("role", "user")
      .order("created_at", { ascending: true }).order("id", { ascending: true })
      .limit(1).maybeSingle();
    if (previewError) { previewUnavailable = true; return session; }
    if (first) {
      if (first.user_id !== userId || first.session_id !== session.id) throw new MirrorHistoryError("ownership_mismatch");
      if (typeof first.content !== "string") throw new MirrorHistoryError("invalid_response");
      if (!first.content.trimStart().startsWith("__VYV_INTERNAL_")) session.preview = first.content.slice(0, 240);
    }
    return session;
  }));
  const last = selected[selected.length - 1];
  return { sessions, hasMore, previewUnavailable,
    nextCursor: hasMore && last ? { userId, updatedAt: last.updated_at as string, id: last.id as string } : null };
}

/**
 * Read the newest bounded page, returning its visible messages in chronological
 * order. Older pages use created_at/id and are prepended by the UI. Ownership
 * and the shared Mirror kind are verified before any message request is sent.
 */
export async function loadMirrorMessagePage(client: HistoryClient, options: {
  userId: string; sessionId: string; cursor?: MirrorMessageCursor | null; pageSize?: number;
}): Promise<MirrorMessagePage> {
  const { userId, sessionId, cursor } = options;
  assertId(userId); assertId(sessionId);
  const size = pageSize(options.pageSize, 50, 100);
  if (cursor) {
    assertId(cursor.id);
    if (cursor.userId !== userId || cursor.sessionId !== sessionId || !isTimestamp(cursor.createdAt)) throw new MirrorHistoryError("invalid_request");
  }
  const { data: session, error: sessionError } = await client.from("mirror_sessions")
    .select("id, user_id, kind").eq("user_id", userId).eq("id", sessionId).eq("kind", "mirror").maybeSingle();
  if (sessionError || !session) throw new MirrorHistoryError("session_unavailable");
  ownedSession(session, userId, sessionId);
  let query = client.from("mirror_messages")
    .select("id, user_id, session_id, role, content, created_at")
    .eq("user_id", userId).eq("session_id", sessionId).in("role", ["user", "assistant"])
    .order("created_at", { ascending: false }).order("id", { ascending: false })
    .limit(size + 1);
  if (cursor) query = query.or(`created_at.lt.${cursor.createdAt},and(created_at.eq.${cursor.createdAt},id.lt.${cursor.id})`);
  const { data, error } = await query;
  if (error) throw new MirrorHistoryError("load_failed");
  const allRows = rows(data);
  for (const row of allRows) {
    if (row.user_id !== userId || row.session_id !== sessionId) throw new MirrorHistoryError("ownership_mismatch");
    if (typeof row.id !== "string" || !UUID.test(row.id) || !isTimestamp(row.created_at) ||
      (row.role !== "user" && row.role !== "assistant") || typeof row.content !== "string") throw new MirrorHistoryError("invalid_response");
  }
  validatePageOrder(allRows, "created_at", size + 1, cursor ? { timestamp: cursor.createdAt, id: cursor.id } : null);
  const selected = allRows.slice(0, size);
  const hasMore = allRows.length > size;
  const last = selected[selected.length - 1];
  const messages = selected.filter(row => !(row.content as string).trimStart().startsWith("__VYV_INTERNAL_")).reverse()
    .map(row => ({ id: row.id as string, role: row.role as "user" | "assistant", content: row.content as string, created_at: row.created_at as string }));
  return { messages, hasMore,
    nextCursor: hasMore && last ? { userId, sessionId, createdAt: last.created_at as string, id: last.id as string } : null };
}
